import { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import { 
  Clock, 
  Play, 
  Square, 
  Timer,
  CheckCircle,
  AlertCircle,
  Coffee,
  Sun,
  Sunrise,
  Pause,
  XCircle,
  CalendarX
} from 'lucide-react';

const MyShiftPage = () => {
  const { API_URL, getAuthHeader, user } = useAuth();
  const { t } = useLanguage();
  
  const [todayData, setTodayData] = useState(null);
  const [tomorrowData, setTomorrowData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [clockingIn, setClockingIn] = useState(false);
  const [clockingOut, setClockingOut] = useState(false);
  const [togglingBreak, setTogglingBreak] = useState(false);
  const [elapsedTime, setElapsedTime] = useState(0);
  const timerRef = useRef(null);
  
  // Absence Dialog State
  const [absenceDialogOpen, setAbsenceDialogOpen] = useState(false);
  const [absenceReason, setAbsenceReason] = useState('');
  const [absenceDate, setAbsenceDate] = useState('today'); // 'today' or 'tomorrow'
  const [reportingAbsence, setReportingAbsence] = useState(false);

  useEffect(() => {
    fetchShifts();
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, []);

  // Start timer when clocked in (excluding break time)
  useEffect(() => {
    if (todayData?.is_clocked_in && todayData?.attendance?.clock_in && !todayData?.is_on_break) {
      const clockInTime = new Date(todayData.attendance.clock_in);
      const totalBreakSeconds = (todayData.attendance?.total_break_minutes || 0) * 60;
      
      const updateElapsed = () => {
        const now = new Date();
        const diff = Math.floor((now - clockInTime) / 1000) - totalBreakSeconds;
        setElapsedTime(Math.max(0, diff));
      };
      
      updateElapsed();
      timerRef.current = setInterval(updateElapsed, 1000);
      
      return () => clearInterval(timerRef.current);
    } else if (todayData?.is_on_break) {
      // Pause timer during break
      if (timerRef.current) clearInterval(timerRef.current);
    } else {
      setElapsedTime(0);
      if (timerRef.current) clearInterval(timerRef.current);
    }
  }, [todayData?.is_clocked_in, todayData?.attendance?.clock_in, todayData?.is_on_break]);

  const fetchShifts = async () => {
    setLoading(true);
    try {
      const [todayRes, tomorrowRes] = await Promise.all([
        axios.get(`${API_URL}/api/my-shift/today`, { headers: getAuthHeader() }),
        axios.get(`${API_URL}/api/my-shift/tomorrow`, { headers: getAuthHeader() }).catch(() => ({ data: null }))
      ]);
      setTodayData(todayRes.data);
      setTomorrowData(tomorrowRes.data);
    } catch (error) {
      console.error('Error fetching shifts:', error);
      toast.error('حدث خطأ في تحميل بيانات الدوام');
    } finally {
      setLoading(false);
    }
  };

  const handleClockIn = async () => {
    setClockingIn(true);
    try {
      const res = await axios.post(`${API_URL}/api/attendance/clock-in`, {}, { headers: getAuthHeader() });
      if (res.data.late_minutes > 0) {
        toast.warning(`تم تسجيل بدء الدوام - متأخر ${res.data.late_minutes} دقيقة ⏰`);
      } else {
        toast.success('تم تسجيل بدء الدوام ✅');
      }
      fetchShifts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'حدث خطأ');
    } finally {
      setClockingIn(false);
    }
  };

  const handleClockOut = async () => {
    setClockingOut(true);
    try {
      const res = await axios.post(`${API_URL}/api/attendance/clock-out`, {}, { headers: getAuthHeader() });
      toast.success(res.data.message);
      fetchShifts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'حدث خطأ');
    } finally {
      setClockingOut(false);
    }
  };

  const handleToggleBreak = async () => {
    setTogglingBreak(true);
    try {
      const res = await axios.post(`${API_URL}/api/attendance/toggle-break`, {}, { headers: getAuthHeader() });
      toast.success(res.data.message);
      fetchShifts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'حدث خطأ');
    } finally {
      setTogglingBreak(false);
    }
  };

  const handleReportAbsence = async () => {
    setReportingAbsence(true);
    try {
      const targetDate = absenceDate === 'tomorrow' 
        ? new Date(Date.now() + 86400000).toISOString().split('T')[0]
        : new Date().toISOString().split('T')[0];
      
      const res = await axios.post(
        `${API_URL}/api/attendance/report-absence`,
        { reason: absenceReason, date: targetDate },
        { headers: getAuthHeader() }
      );
      toast.success(res.data.message);
      setAbsenceDialogOpen(false);
      setAbsenceReason('');
      fetchShifts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'حدث خطأ');
    } finally {
      setReportingAbsence(false);
    }
  };

  const formatTime = (seconds) => {
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return `${String(hrs).padStart(2, '0')}:${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
  };

  const formatTimeString = (timeStr) => {
    if (!timeStr) return '-';
    try {
      const date = new Date(timeStr);
      return date.toLocaleTimeString('ar', { hour: '2-digit', minute: '2-digit' });
    } catch {
      return timeStr;
    }
  };

  const formatDateArabic = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    const days = ['الأحد', 'الاثنين', 'الثلاثاء', 'الأربعاء', 'الخميس', 'الجمعة', 'السبت'];
    const months = ['يناير', 'فبراير', 'مارس', 'أبريل', 'مايو', 'يونيو', 'يوليو', 'أغسطس', 'سبتمبر', 'أكتوبر', 'نوفمبر', 'ديسمبر'];
    return `${days[date.getDay()]} ${date.getDate()} ${months[date.getMonth()]} ${date.getFullYear()}`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-gradient-to-br from-amber-50 to-orange-50">
        <div className="text-center">
          <div className="animate-spin w-12 h-12 border-4 border-amber-600 border-t-transparent rounded-full mx-auto"></div>
          <p className="mt-4 text-lg text-stone-600">جاري تحميل بيانات الدوام...</p>
        </div>
      </div>
    );
  }

  const hasShiftToday = todayData?.shift !== null && todayData?.shift !== undefined;
  const hasShiftTomorrow = tomorrowData?.shift !== null && tomorrowData?.shift !== undefined;
  const isClockedIn = todayData?.is_clocked_in === true;
  const isOnBreak = todayData?.is_on_break === true;
  const hasCompletedShift = todayData?.attendance !== null && todayData?.attendance?.clock_out !== null;

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 to-orange-50 p-4">
      <div className="max-w-2xl mx-auto space-y-6">
        {/* Header */}
        <div className="text-center py-6">
          <div className="w-24 h-24 bg-gradient-to-br from-amber-500 to-orange-500 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
            <Clock className="w-12 h-12 text-white" />
          </div>
          <h1 className="text-3xl font-bold text-stone-800">دوامي</h1>
          <p className="text-xl text-stone-600 font-medium mt-2">مرحباً، {user?.name}</p>
        </div>

        {/* Timer Card - Show when clocked in */}
        {isClockedIn && !hasCompletedShift && (
          <Card className={`text-white shadow-xl border-0 ${isOnBreak ? 'bg-gradient-to-br from-yellow-500 to-amber-600' : 'bg-gradient-to-br from-green-500 to-emerald-600'}`}>
            <CardContent className="p-8 text-center">
              {isOnBreak ? (
                <>
                  <Coffee className="w-14 h-14 mx-auto mb-4 opacity-90 animate-bounce" />
                  <p className="text-xl opacity-90 mb-2">أنت في استراحة</p>
                  <p className="text-4xl font-bold mb-2">☕ استرح قليلاً</p>
                  <p className="text-lg opacity-80">
                    مدة الاستراحة الكلية: {todayData?.attendance?.total_break_minutes || 0} دقيقة
                  </p>
                </>
              ) : (
                <>
                  <Timer className="w-14 h-14 mx-auto mb-4 opacity-90" />
                  <p className="text-xl opacity-90 mb-2">الوقت المنقضي</p>
                  <p className="text-6xl font-mono font-bold tracking-wider">
                    {formatTime(elapsedTime)}
                  </p>
                  <p className="text-lg opacity-80 mt-4">
                    بدأت الدوام: {formatTimeString(todayData?.attendance?.clock_in)}
                  </p>
                  {todayData?.attendance?.total_break_minutes > 0 && (
                    <p className="text-sm opacity-70 mt-2">
                      (تم خصم {todayData?.attendance?.total_break_minutes} دقيقة استراحة)
                    </p>
                  )}
                </>
              )}
            </CardContent>
          </Card>
        )}

        {/* Completed Shift Card */}
        {hasCompletedShift && (
          <Card className="bg-gradient-to-br from-blue-500 to-indigo-600 text-white shadow-xl border-0">
            <CardContent className="p-8 text-center">
              <CheckCircle className="w-14 h-14 mx-auto mb-4 opacity-90" />
              <p className="text-xl opacity-90 mb-2">أكملت دوامك اليوم</p>
              <p className="text-5xl font-bold">
                {todayData?.attendance?.total_hours?.toFixed(1)} ساعة
              </p>
              <div className="flex justify-center gap-12 mt-6 text-lg opacity-90">
                <div>
                  <p className="opacity-75">البدء</p>
                  <p className="font-bold text-2xl">{formatTimeString(todayData?.attendance?.clock_in)}</p>
                </div>
                <div>
                  <p className="opacity-75">الانتهاء</p>
                  <p className="font-bold text-2xl">{formatTimeString(todayData?.attendance?.clock_out)}</p>
                </div>
              </div>
              {todayData?.attendance?.total_break_minutes > 0 && (
                <p className="text-sm opacity-70 mt-4">
                  استراحة: {todayData?.attendance?.total_break_minutes} دقيقة
                </p>
              )}
            </CardContent>
          </Card>
        )}

        {/* TODAY'S SHIFT - Big Bold Display */}
        <Card className="border-2 border-amber-400 shadow-lg bg-white">
          <CardHeader className="bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-t-lg">
            <CardTitle className="flex items-center gap-3 text-2xl">
              <Sun className="w-8 h-8" />
              دوام اليوم
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            {hasShiftToday ? (
              <>
                {/* Date - Big and Bold */}
                <div className="text-center mb-6 bg-amber-50 py-4 rounded-xl">
                  <p className="text-3xl font-bold text-amber-800">
                    {formatDateArabic(todayData.date)}
                  </p>
                </div>
                
                {/* Time - Very Big and Bold */}
                <div className="flex items-center justify-around py-6 bg-stone-50 rounded-xl">
                  <div className="text-center">
                    <p className="text-stone-500 text-lg mb-2">وقت البدء</p>
                    <p className="text-5xl font-bold text-green-600">{todayData.shift.start_time}</p>
                  </div>
                  <div className="text-4xl text-stone-300">←</div>
                  <div className="text-center">
                    <p className="text-stone-500 text-lg mb-2">وقت الانتهاء</p>
                    <p className="text-5xl font-bold text-red-600">{todayData.shift.end_time}</p>
                  </div>
                </div>
                
                {todayData.shift.notes && (
                  <p className="text-center text-stone-600 text-lg mt-4 bg-amber-50 p-3 rounded-lg">
                    📝 {todayData.shift.notes}
                  </p>
                )}
              </>
            ) : (
              <div className="text-center py-8">
                <Coffee className="w-20 h-20 mx-auto mb-4 text-stone-300" />
                <p className="text-2xl text-stone-500 font-medium">لا يوجد دوام محدد لك اليوم</p>
                <p className="text-stone-400 mt-2">تواصل مع المسؤول إذا كان هناك خطأ</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* TOMORROW'S SHIFT - Big Bold Display */}
        <Card className="border-2 border-blue-400 shadow-lg bg-white">
          <CardHeader className="bg-gradient-to-r from-blue-500 to-indigo-500 text-white rounded-t-lg">
            <CardTitle className="flex items-center gap-3 text-2xl">
              <Sunrise className="w-8 h-8" />
              دوام الغد
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            {hasShiftTomorrow ? (
              <>
                {/* Date - Big and Bold */}
                <div className="text-center mb-6 bg-blue-50 py-4 rounded-xl">
                  <p className="text-3xl font-bold text-blue-800">
                    {formatDateArabic(tomorrowData.date)}
                  </p>
                </div>
                
                {/* Time - Very Big and Bold */}
                <div className="flex items-center justify-around py-6 bg-stone-50 rounded-xl">
                  <div className="text-center">
                    <p className="text-stone-500 text-lg mb-2">وقت البدء</p>
                    <p className="text-5xl font-bold text-green-600">{tomorrowData.shift.start_time}</p>
                  </div>
                  <div className="text-4xl text-stone-300">←</div>
                  <div className="text-center">
                    <p className="text-stone-500 text-lg mb-2">وقت الانتهاء</p>
                    <p className="text-5xl font-bold text-red-600">{tomorrowData.shift.end_time}</p>
                  </div>
                </div>
                
                {tomorrowData.shift.notes && (
                  <p className="text-center text-stone-600 text-lg mt-4 bg-blue-50 p-3 rounded-lg">
                    📝 {tomorrowData.shift.notes}
                  </p>
                )}
              </>
            ) : (
              <div className="text-center py-8">
                <AlertCircle className="w-16 h-16 mx-auto mb-4 text-stone-300" />
                <p className="text-xl text-stone-500">لم يتم تحديد دوام الغد بعد</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Clock In/Out/Break Buttons - Only show if today has shift and not completed */}
        {hasShiftToday && !hasCompletedShift && (
          <div className="flex flex-col gap-4 py-4">
            {!isClockedIn ? (
              <Button
                onClick={handleClockIn}
                disabled={clockingIn}
                size="lg"
                className="bg-green-600 hover:bg-green-700 text-white px-12 py-8 text-2xl gap-4 rounded-2xl shadow-lg w-full"
              >
                {clockingIn ? (
                  <span className="animate-spin w-8 h-8 border-4 border-white border-t-transparent rounded-full" />
                ) : (
                  <Play className="w-8 h-8" />
                )}
                بدء الدوام
              </Button>
            ) : (
              <>
                {/* Break Button */}
                <Button
                  onClick={handleToggleBreak}
                  disabled={togglingBreak}
                  size="lg"
                  variant={isOnBreak ? "default" : "secondary"}
                  className={`px-12 py-6 text-xl gap-4 rounded-2xl shadow-lg w-full ${
                    isOnBreak 
                      ? 'bg-amber-500 hover:bg-amber-600 text-white' 
                      : 'bg-yellow-100 hover:bg-yellow-200 text-yellow-800 border-2 border-yellow-300'
                  }`}
                >
                  {togglingBreak ? (
                    <span className="animate-spin w-6 h-6 border-3 border-current border-t-transparent rounded-full" />
                  ) : isOnBreak ? (
                    <Play className="w-6 h-6" />
                  ) : (
                    <Coffee className="w-6 h-6" />
                  )}
                  {isOnBreak ? 'العودة من الاستراحة' : 'استراحة'}
                </Button>

                {/* End Shift Button */}
                <Button
                  onClick={handleClockOut}
                  disabled={clockingOut || isOnBreak}
                  size="lg"
                  variant="destructive"
                  className="px-12 py-8 text-2xl gap-4 rounded-2xl shadow-lg w-full"
                >
                  {clockingOut ? (
                    <span className="animate-spin w-8 h-8 border-4 border-white border-t-transparent rounded-full" />
                  ) : (
                    <Square className="w-8 h-8" />
                  )}
                  انتهاء الدوام
                </Button>
                {isOnBreak && (
                  <p className="text-center text-amber-600 text-sm">عد من الاستراحة أولاً لإنهاء الدوام</p>
                )}
              </>
            )}
          </div>
        )}

        {/* Cannot Come Button - Show when has shift today/tomorrow and not clocked in */}
        {(hasShiftToday || hasShiftTomorrow) && !isClockedIn && !hasCompletedShift && (
          <Card className="border-2 border-red-200 bg-red-50">
            <CardContent className="p-4">
              <Button
                onClick={() => setAbsenceDialogOpen(true)}
                variant="outline"
                size="lg"
                className="w-full border-red-300 text-red-600 hover:bg-red-100 hover:text-red-700 py-6 text-lg gap-3"
              >
                <CalendarX className="w-6 h-6" />
                لا أستطيع القدوم
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Absence Dialog */}
        <Dialog open={absenceDialogOpen} onOpenChange={setAbsenceDialogOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="text-xl flex items-center gap-2 text-red-600">
                <CalendarX className="w-6 h-6" />
                الإبلاغ عن عدم الحضور
              </DialogTitle>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              {/* Select Day */}
              <div className="space-y-2">
                <Label>اليوم</Label>
                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant={absenceDate === 'today' ? 'default' : 'outline'}
                    className={absenceDate === 'today' ? 'bg-red-600 hover:bg-red-700' : ''}
                    onClick={() => setAbsenceDate('today')}
                  >
                    <Sun className="w-4 h-4 me-2" />
                    اليوم
                  </Button>
                  <Button
                    type="button"
                    variant={absenceDate === 'tomorrow' ? 'default' : 'outline'}
                    className={absenceDate === 'tomorrow' ? 'bg-red-600 hover:bg-red-700' : ''}
                    onClick={() => setAbsenceDate('tomorrow')}
                  >
                    <Sunrise className="w-4 h-4 me-2" />
                    الغد
                  </Button>
                </div>
              </div>
              
              {/* Reason */}
              <div className="space-y-2">
                <Label>السبب (اختياري)</Label>
                <Textarea
                  value={absenceReason}
                  onChange={(e) => setAbsenceReason(e.target.value)}
                  placeholder="اكتب سبب الغياب..."
                  rows={3}
                  className="resize-none"
                />
              </div>
            </div>
            
            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={() => setAbsenceDialogOpen(false)}>
                إلغاء
              </Button>
              <Button 
                onClick={handleReportAbsence}
                disabled={reportingAbsence}
                className="bg-red-600 hover:bg-red-700"
              >
                {reportingAbsence ? (
                  <span className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full me-2" />
                ) : (
                  <XCircle className="w-4 h-4 me-2" />
                )}
                تأكيد الغياب
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default MyShiftPage;
